import type { KeyboardEvent, ReactNode } from 'react';
import type {
    Control,
    FieldErrors,
    FieldValues,
    Path,
    UseFormRegister
} from 'react-hook-form';
import type { IntlShape } from 'react-intl';

/**
 * What a record form's field renderers need from the form they belong to.
 *
 * Handed over ONCE (see `useFormFields`) instead of threaded through every
 * call, which is what let each dialog grow its own `text()`/`suggest()` pair.
 *
 * The renderers built on it are plain functions returning JSX, not components,
 * so every field stays part of the FORM's render pass — as components each
 * would get a fiber and a reconciliation identity of its own. The exception is
 * the recommended-empty hint on a registered text field, which needs a
 * subscription of its own; see ./recommendedFieldWarning.
 */
export interface FormFieldBinding<Values extends FieldValues> {
    intl: IntlShape;
    register: UseFormRegister<Values>;
    control: Control<Values>;
    errors: FieldErrors<Values>;
    /** Turns a validation code into the message shown under the field. */
    validationMessage: (raw: string | undefined) => string;
    /**
     * The i18n id of a field's label. A FUNCTION, not a prefix: the Profil form
     * labels its Bodenmerkmale from the Planum catalog, so the two can't drift.
     * Use `labelIdsFrom` for the ordinary one-catalog case. A plain string, not
     * a `Path<Values>`, because a section also labels things that are not form
     * fields — the stratigraphy pickers, held outside the form.
     */
    labelId: (name: string) => string;
    /**
     * Soft-required: while the field is empty it carries an amber hint. Not
     * validation — nothing blocks saving.
     */
    recommended?: (name: Path<Values>) => boolean;
    /**
     * The i18n id of the unit a field states next to its value
     * ('common.centimetres'), or undefined for a field that states none.
     */
    unitId?: (name: Path<Values>) => string | undefined;
    /**
     * F2 on a field: opens the list of the values it last held. Undefined for a
     * field with nothing to offer, absent altogether in a form that does not
     * wire it — see ./useFormRecentValues.
     */
    recentKeyDown?: (
        name: string
    ) => ((event: KeyboardEvent<HTMLElement>) => void) | undefined;
    /** The same list as a button, for the thumbs. Null where there is none. */
    recentTrigger?: (name: string) => ReactNode;
    /**
     * Every field shown but not editable — for a reader who may see the record
     * but not change it. Read-only rather than disabled, so the text can still
     * be selected and copied; the form wraps itself in `readOnlySheetSx`.
     */
    readOnly?: boolean;
}

/** Labels every field from ONE catalog: `${prefix}.${name}`. */
export function labelIdsFrom(prefix: string): (name: string) => string {
    return (name) => `${prefix}.${name}`;
}

/**
 * A field's error as react-hook-form recorded it. Indexing by name is safe
 * because every record form here is FLAT — no `a.b` paths, no field arrays.
 */
export function fieldError<Values extends FieldValues>(
    errors: FieldErrors<Values>,
    name: Path<Values>
): { message?: string } | undefined {
    return (errors as Record<string, { message?: string } | undefined>)[name];
}

/**
 * The F2 handler as props for an Autocomplete — spread, never passed as
 * `onKeyDown={undefined}`. MUI merges the Autocomplete's own props over the
 * handlers it builds for its root, and an explicit `undefined` wins: the arrow
 * keys and Enter then never reach the list, and Enter submits the form.
 */
export function recentKeyDownProps<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>
): { onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void } {
    const onKeyDown = binding.recentKeyDown?.(name);
    return onKeyDown ? { onKeyDown } : {};
}

export function showsRecommendedHint<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    value: unknown
): boolean {
    if (!binding.recommended?.(name)) {
        return false;
    }
    return !value;
}
